import { useEffect, useState } from "react";
import { onPredictiveAlert, type PredictiveAlert } from "@/lib/pr26/predictiveAlerts";
import { triggerEngagement } from "@/lib/pr26/hapticsAndSound";
import { useDelayedBySignal } from "@/lib/sync/offsetOrchestration";

/**
 * PR-26 — Floating predictive alert toast. Subscribes to the alert bus and
 * fires haptics/sound on goal-probability spikes.
 */
export function PredictiveAlertToast() {
  const [alert, setAlert] = useState<PredictiveAlert | undefined>();

  useEffect(() => {
    return onPredictiveAlert((a) => {
      setAlert(a);
      if (a.kind === "goal_probability_high" || a.kind === "possible_penalty") {
        triggerEngagement("important_push");
      }
      window.setTimeout(() => setAlert((cur) => (cur?.alert_id === a.alert_id ? undefined : cur)), 6_000);
    });
  }, []);

  // Phase 1 — high-stakes goal-probability alerts ride the goal_alert weight,
  // everything else flows on radar_alert (lighter delay, preserves edge).
  const signalKind =
    alert?.kind === "goal_probability_high" || alert?.kind === "possible_penalty"
      ? ("goal_alert" as const)
      : ("radar_alert" as const);
  const delayedAlert = useDelayedBySignal(alert, signalKind);
  if (!delayedAlert) return null;
  const a = delayedAlert;
  return (
    <div className="pointer-events-none fixed bottom-24 left-1/2 z-50 w-[min(360px,90vw)] -translate-x-1/2 rounded-xl border border-[oklch(0.65_0.25_25)]/60 bg-background/95 px-3 py-2 text-[12px] font-bold backdrop-blur-md shadow-2xl animate-fade-in">
      <div className="flex items-center justify-between">
        <span className="text-[oklch(0.65_0.25_25)]">⚠ {a.title}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{Math.round(a.confidence * 100)}%</span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{a.body}</p>
    </div>
  );
}